import {
  DEFAULT_MACHINE_CREDENTIAL_MODULE_KEYS,
  type CreateMachineCredentialResponse,
  type ListMachineCredentialsResponse,
  type MachineCredentialSummary,
} from '@protoss/shared';
import { Button, SkeletonBlock, TextField } from '@protoss/ui';
import * as React from 'react';
import { apiFetch } from '../../lib/apiClient';
import './api-keys-tab.css';

/**
 * API keys (machine credentials) - workspace admin surface.
 *
 * Two behaviours here are deliberate and easy to "fix" into bugs:
 *
 * 1. The token is shown ONCE, right after creation, and is not retrievable
 *    afterwards. There is no reveal endpoint because the server stores only a hash.
 *    The UI has to make that unmistakable, since the recovery path is "create another
 *    key", not "look it up".
 *
 * 2. Creating a key does NOT invalidate existing ones. That is what allows
 *    zero-downtime rotation - create, update the client, then revoke the old key. A
 *    single "regenerate" button would silently break an unattended run the moment an
 *    admin pressed it mid-day, so the flow is explicitly two steps.
 */

const ENDPOINT = '/api/workspace/admin/machine-credentials';

function formatWhen(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function StatusPill({ status }: { status: MachineCredentialSummary['status'] }) {
  return <span className={`api-keys__status api-keys__status--${status}`}>{status}</span>;
}

/** The one-time token reveal. Rendered only immediately after a create. */
function NewKeyBanner({ token, onDismiss }: { token: string; onDismiss: () => void }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    void navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  }, [token]);

  return (
    <div className="api-keys__reveal" role="alert">
      <p className="api-keys__reveal-title">Copy this key now</p>
      <p className="api-keys__reveal-note">
        This is the only time it will be shown. It is stored hashed and cannot be retrieved again -
        if you lose it, create a new key and revoke this one.
      </p>
      <code className="api-keys__reveal-token">{token}</code>
      <div className="api-keys__reveal-actions">
        <Button size="sm" onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy key'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          I have saved it
        </Button>
      </div>
    </div>
  );
}

export function ApiKeysTab() {
  const [credentials, setCredentials] = React.useState<MachineCredentialSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [name, setName] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [newToken, setNewToken] = React.useState<string | null>(null);
  const [revoking, setRevoking] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch<ListMachineCredentialsResponse>(ENDPOINT);
      setCredentials(res.credentials);
    } catch {
      setError('Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = React.useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || creating) return;

    setCreating(true);
    setError(null);
    try {
      const res = await apiFetch<CreateMachineCredentialResponse>(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmed,
          moduleKeys: [...DEFAULT_MACHINE_CREDENTIAL_MODULE_KEYS],
        }),
      });
      setNewToken(res.token);
      setName('');
      await load();
    } catch {
      setError('Failed to create the key.');
    } finally {
      setCreating(false);
    }
  }, [name, creating, load]);

  const handleRevoke = React.useCallback(
    async (credential: MachineCredentialSummary) => {
      // Revocation is immediate and cannot be undone, and any client still using this
      // key stops working at once - so confirm against the name the admin recognises.
      const confirmed = window.confirm(
        `Revoke "${credential.name}"?\n\nAny client still using this key will stop working immediately. This cannot be undone.`
      );
      if (!confirmed) return;

      setRevoking(credential.uuid);
      setError(null);
      try {
        await apiFetch(`${ENDPOINT}/${credential.uuid}`, { method: 'DELETE' });
        await load();
      } catch {
        setError('Failed to revoke the key.');
      } finally {
        setRevoking(null);
      }
    },
    [load]
  );

  if (loading) return <SkeletonBlock height="160px" />;

  return (
    <div className="api-keys">
      <p className="api-keys__intro">
        API keys let an external client - such as the coSow MCP server or a scheduled automation -
        call coSow without a browser session. A key acts as a dedicated service identity with full
        access to this workspace, never as the person who created it.
      </p>

      <div className="api-keys__create">
        <TextField
          label="Key name"
          placeholder="e.g. Nightly board sync"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleCreate();
          }}
        />
        <Button onClick={() => void handleCreate()} disabled={!name.trim() || creating}>
          {creating ? 'Creating...' : 'Create key'}
        </Button>
      </div>

      {newToken && <NewKeyBanner token={newToken} onDismiss={() => setNewToken(null)} />}
      {error && <p className="api-keys__error">{error}</p>}

      {credentials.length === 0 ? (
        <p className="api-keys__empty">No API keys yet.</p>
      ) : (
        <table className="api-keys__table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Key ID</th>
              <th scope="col">Status</th>
              <th scope="col">Created</th>
              <th scope="col">Last used</th>
              <th scope="col">Expires</th>
              <th scope="col" className="api-keys__col-actions">
                <span className="api-keys__sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((c) => (
              <tr key={c.uuid} className={c.status !== 'active' ? 'api-keys__row--inactive' : ''}>
                <td>{c.name}</td>
                {/* The public lookup segment - safe to display, and how an admin matches
                    a row to the key configured in a client. */}
                <td>
                  <code className="api-keys__token-id">{c.tokenId}</code>
                </td>
                <td>
                  <StatusPill status={c.status} />
                </td>
                <td>{formatWhen(c.createdAt)}</td>
                {/* "Never" is the signal that a key is safe to revoke. */}
                <td>{formatWhen(c.lastUsedAt)}</td>
                {/* "Never" here means a legacy key minted before default expiry existed. */}
                <td>{formatWhen(c.expiresAt)}</td>
                <td className="api-keys__col-actions">
                  {c.status === 'active' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void handleRevoke(c)}
                      disabled={revoking === c.uuid}
                    >
                      {revoking === c.uuid ? 'Revoking...' : 'Revoke'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="api-keys__rotation">
        <strong>To rotate a key:</strong> create the new one, update your client, then revoke the
        old one. Creating a key never invalidates an existing key, so rotation costs no downtime.
      </p>
    </div>
  );
}
